import { Link } from "react-router-dom"
import { Button } from "react-bootstrap"
import moment from "moment"
import { Copy } from "lucide-react"
import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import LoadingWrapper from "~/library/LoadingWrapper"
import { apiBookings } from "~/routes"
import CatchTable from "~/library/CatchTable"
import type Booking from "~/types/Booking"
import { capitalize } from "~/utils/helpers"
import Pagination from "~/library/Pagination"
import { useToast } from "~/utils/Toast"
import NavigationLink from "~/library/NavigationLink"

const Dashboard = () => {
  const { venue, venueSlug } = getAppConfig()
  const toast = useToast()

  const today = moment().format("YYYY-MM-DD")
  const tomorrow = moment().add(1, "day").format("YYYY-MM-DD")

  const {
    data: todaysBookings,
    isLoading: todaysLoading,
    refetch: refetchTodays,
    metadata: todaysMetadata,
  } = useFetchResource<{ bookings: Booking[] }, "bookings">(
    apiBookings.index.path({ venueSlug }),
    "bookings",
    {
      page: "1",
      "q[date_eq]": today,
    }
  )

  const {
    data: tomorrowsBookings,
    isLoading: tomorrowsLoading,
    refetch: refetchTomorrows,
    metadata: tomorrowsMetadata,
  } = useFetchResource<{ bookings: Booking[] }, "bookings">(
    apiBookings.index.path({ venueSlug }),
    "bookings",
    {
      page: "1",
      "q[date_eq]": tomorrow,
    }
  )

  const handleTodaysPageChange = (page: string) => {
    refetchTodays({ page: page, "q[date_eq]": today })
  }

  const handleTomorrowsPageChange = (page: string) => {
    refetchTomorrows({
      page: page,
      "q[date_eq]": tomorrow,
    })
  }

  const formatStatus = (status: string) => {
    if (status === "pending") {
      return <span className="badge badge-lg bg-warning">Pending</span>
    } else if (status === "confirmed") {
      return <span className="badge badge-lg bg-success">Confirmed</span>
    } else {
      return capitalize(status)
    }
  }

  const copyVenueLink = async () => {
    const venueUrl = `${window.location.origin}/venues/${venue.slug}`
    try {
      await navigator.clipboard.writeText(venueUrl)
      toast("Venue link copied to clipboard!", "success")
    } catch (err) {
      toast("Failed to copy link", "danger")
    }
  }

  const bookingTableColumns = [
    {
      key: "name",
      header: "Name",
      formatter: (name: string, data: Booking) => (
        <NavigationLink type="bookings" to={`/bookings/${data.id}`}>
          {name}
        </NavigationLink>
      ),
    },
    {
      key: "client",
      header: "Client Name",
      formatter: (client: Booking["client"]) =>
        client ? (
          <NavigationLink type="clients" to={`/clients/${client.id}`}>
            {client.name}
          </NavigationLink>
        ) : (
          ""
        ),
    },
    {
      key: "area",
      header: "Area",
      formatter: (area: Booking["area"]) =>
        area ? (
          <NavigationLink type="areas" to={`/areas/${area.id}`}>
            {area.name}
          </NavigationLink>
        ) : (
          ""
        ),
    },
    {
      key: "status",
      header: "Status",
      formatter: (status: Booking["status"]) => formatStatus(status),
    },
    {
      key: "action",
      header: "Action",
      formatter: (_: null, data: Booking) => (
        <NavigationLink type="bookings" to={`/bookings/${data.id}`}>
          <Button variant="primary" size="sm">
            View
          </Button>
        </NavigationLink>
      ),
    },
  ]

  return (
    <div className="w-full h-full p-3">
      <div className="d-flex align-items-center mb-3">
        {venue.logoUrl && (
          <img
            src={venue.logoUrl}
            alt={venue.logo?.alt}
            title={venue.logo?.title}
            className="me-3"
            style={{ maxHeight: 128 }}
          />
        )}

        <h2 className="text-2xl m-0">{venue.name}</h2>
        <Link to="/settings/venue" className="ms-auto">
          <Button variant="outline-primary">Edit Venue</Button>
        </Link>
      </div>
      <div className="mb-4">
        <div className="d-flex align-items-center">
          <span className="me-2">Venue Page:</span>
          <div className="d-flex align-items-center p-2 border border-light custom-block rounded">
            <span className="me-2">
              {window.location.host}/venues/{venue.slug}
            </span>
            <Copy
              style={{ maxWidth: "fit-content", cursor: "pointer" }}
              onClick={copyVenueLink}
              size={16}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-2">Today&apos;s Bookings</h4>
              <LoadingWrapper isLoading={todaysLoading}>
                <CatchTable
                  data={todaysBookings}
                  emptyMessage="No Bookings Today"
                  columns={bookingTableColumns}
                />
                {todaysMetadata && (
                  <Pagination
                    metadata={todaysMetadata}
                    onPageChange={(page: number) => handleTodaysPageChange(page.toString())}
                  />
                )}
              </LoadingWrapper>
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-2">Tomorrow&apos;s Bookings</h4>
              <LoadingWrapper isLoading={tomorrowsLoading}>
                <CatchTable
                  data={tomorrowsBookings}
                  emptyMessage="No Bookings Tomorrow"
                  columns={bookingTableColumns}
                />
                {tomorrowsMetadata && (
                  <Pagination
                    metadata={tomorrowsMetadata}
                    onPageChange={(page: number) => handleTomorrowsPageChange(page.toString())}
                  />
                )}
              </LoadingWrapper>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
